import { useState } from 'react';
import { MoreHorizontal, Trash2, RefreshCw, GripVertical, Maximize2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartRenderer } from '../ResultsCard/ChartRenderer';
import { CardSizeSelector } from './CardSizeSelector';
import type { SavedCard, QueryResult } from '../../types/analytics.types';
import type { CardSizePreset } from '../../types/dashboard.types';
import { getCardSizePreset } from '../../types/dashboard.types';
import { cn } from '@/lib/utils';

interface DashboardCardProps {
  card: SavedCard;
  result: QueryResult | null;
  isLoading: boolean;
  isError: boolean;
  isEditMode: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onRefresh: () => void;
  onSizeChange: (preset: CardSizePreset) => void;
}

export function DashboardCard({
  card,
  result,
  isLoading,
  isError,
  isEditMode,
  isSelected,
  onSelect,
  onDelete,
  onRefresh,
  onSizeChange,
}: DashboardCardProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await onRefresh();
    setIsRefreshing(false);
  };

  const currentPreset = getCardSizePreset(card.position.w, card.position.h);

  return (
    <Card
      className={cn(
        'h-full flex flex-col overflow-hidden transition-shadow',
        isEditMode && 'cursor-move',
        isSelected && isEditMode && 'ring-2 ring-primary ring-offset-2'
      )}
      onClick={isEditMode ? onSelect : undefined}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {isEditMode && (
            <div className="drag-handle cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
              <GripVertical className="h-4 w-4" />
            </div>
          )}
          <CardTitle className="text-sm font-medium truncate">{card.title}</CardTitle>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isEditMode && isSelected && (
            <CardSizeSelector value={currentPreset} onChange={onSizeChange} />
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Card options</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshCw className={cn('mr-2 h-4 w-4', isRefreshing && 'animate-spin')} />
                Refresh
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <Maximize2 className="mr-2 h-4 w-4" />
                Expand
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={onDelete}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Remove from dashboard
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 overflow-hidden">
        {isLoading ? (
          <div className="h-full flex flex-col gap-2">
            <Skeleton className="h-8 w-24" />
            <Skeleton className="flex-1 w-full" />
          </div>
        ) : isError ? (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <p className="text-sm">Failed to load data</p>
          </div>
        ) : result ? (
          <div className="h-full">
            <ChartRenderer result={result} visualizationType={card.visualizationType} />
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <p className="text-sm">No data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
